import React, { useEffect, useState } from 'react';
import { InputAdornment, TextField, Tooltip } from '@mui/material';

/**
 * Eén statistiek als invoerveld: typen is corrigeren. Een gecorrigeerd veld krijgt
 * een gouden rand en houdt die, zodat van de import af zichtbaar blijft dat de
 * waarde met de hand is vastgezet.
 *
 * De correctie gaat pas naar de server als het veld de focus verliest of je Enter
 * indrukt — dan herrekent de hele wedstrijd, en dat wil je niet per toetsaanslag.
 * Een leeg veld betekent: terug naar de API-waarde.
 */
function StatField({
    value, apiValue, corrected, disabled, saving, width = 70, onCommit,
}) {
    const [draft, setDraft] = useState(String(value ?? 0));

    // Een sync of een correctie elders kan de waarde onder je vandaan wijzigen.
    useEffect(() => { setDraft(String(value ?? 0)); }, [value]);

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed === '') {
            if (corrected) onCommit(null);
            else setDraft(String(value ?? 0));
            return;
        }
        const parsed = Number(trimmed);
        if (!Number.isInteger(parsed) || parsed < 0) {
            setDraft(String(value ?? 0));
            return;
        }
        if (parsed === value) return;
        onCommit(parsed);
    };

    const field = (
        <TextField
            type="number"
            size="small"
            value={draft}
            disabled={disabled || saving}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') setDraft(String(value ?? 0));
            }}
            onClick={(event) => event.stopPropagation()}
            slotProps={{
                htmlInput: { min: 0, style: { textAlign: 'center', padding: '6px 4px' } },
                input: corrected ? {
                    endAdornment: <InputAdornment position="end" sx={{ ml: 0, mr: 0.25 }}>✎</InputAdornment>,
                } : undefined,
            }}
            sx={{
                width,
                '& .MuiInputBase-root': {
                    fontSize: 14,
                    ...(corrected ? {
                        color: 'primary.main',
                        fontWeight: 700,
                        backgroundColor: 'rgba(254, 203, 0, 0.15)',
                        '& fieldset': { borderColor: 'primary.main' },
                    } : {}),
                },
                '& .MuiInputAdornment-root': { color: 'primary.main', fontSize: 12 },
            }}
        />
    );

    if (!corrected) return field;

    return (
        <Tooltip title={`Handmatig vastgezet · Sportmonks meldt ${apiValue ?? 0}. Maak het veld leeg om de API-waarde terug te zetten.`}>
            <span>{field}</span>
        </Tooltip>
    );
}

export default StatField;
